import {MiddlewareType} from 'middlewares'
import {
  trackRedirectOfferClicked,
  trackRedirectOfferViewed
} from 'modules/alternativeOffers/actions'
import {
  TRACK_CIO_PAGE_VIEW,
  TRACK_PAGE_VIEW
} from 'modules/analytics/actions/trackPageView'
import initializeAnalytics from 'modules/analytics/initializeSDK'
import {
  getEventProperties,
  getRedirectOffersContexts,
  getSearchContext
} from 'modules/analytics/selectors'
import {getAnonymousId} from 'modules/meta/selectors'
import {logToDataDog} from 'services/datadogLogger'
import {getFullStoryObject, trackFullStoryEvent} from 'services/fullstory'
import {hashEmail} from 'utils/hashEmail'

// Direct import of selector module needed instead of useBrand hook in analytics middleware context where hooks cannot be used.
// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook
import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {
  TRACK_EXPERIMENT_SERVED,
  trackEvent
} from '@vio/core/src/analytics/modules/actions'
import {customIdentify} from '@vio/core/src/analytics/modules/middleware/customIdentify'
import {customerIo} from '@vio/core/src/analytics/services/CustomerIo'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  ExperimentContext,
  Team,
  TrackEventPayload,
  TrackEventProperties
} from '@vio/core/src/analytics/types/Events'
import AnalyticsUtils from '@vio/core/src/analytics/utils'
import {createDataPipelineEventName} from '@vio/core/src/analytics/utils/trackEventHelpers'
import {
  getCognitoIsMemberPlus,
  getIsPrivacyRestrictedUser,
  getUser,
  getUserEmail,
  getUserId,
  getUserIdentifiableTraits
} from '@vio/core/src/auth/modules/selectors'
import {identifyAuthenticatedUser} from '@vio/core/src/auth/modules/slice'
import {
  offersSearchStarted,
  offersSearchSucceeded
} from '@vio/core/src/sapi/services/searchApi/action'
import {getTimezone} from '@vio/core/src/utils/date'
import {logger} from '@vio/core/src/utils/logging/logger'

export enum Component {
  BookingPreviewCard = 'BookingPreviewCard',
  PersonalDetailsForm = 'PersonalDetailsForm',
  BillingDetailsForm = 'BillingDetailsForm',
  PaymentDetailsForm = 'PaymentDetailsForm',
  ImportantInfoCard = 'ImportantInfoCard',
  PostBooking = 'PostBooking',
  Preferences = 'Preferences',
  Language = 'Language',
  Currency = 'Currency',
  PayInCardCurrency = 'PayInCardCurrency'
}

const ANALYTICS_CONFIGS: Partial<
  Record<Team, {endpoint: string | undefined; key: string | undefined}>
> = {
  [Team.Default]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_OTA_ENDPOINT,
    key: process.env.NEXT_PUBLIC_ANALYTICS_OTA_API_KEY
  },
  [Team.Retention]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_RETENTION_ENDPOINT,
    key: process.env.NEXT_PUBLIC_ANALYTICS_RETENTION_API_KEY
  },
  [Team.Shared]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_SHARED_ENDPOINT,
    key: process.env.NEXT_PUBLIC_ANALYTICS_SHARED_API_KEY
  },
  [Team.Select]: {
    endpoint: process.env.NEXT_PUBLIC_ANALYTICS_SELECT_ENDPOINT,
    key: process.env.NEXT_PUBLIC_ANALYTICS_SELECT_API_KEY
  }
}

/**
 * Reset the Segment analytics endpoint to the default endpoint after the endpoint was set to the team endpoint.
 * This is to avoid the analytics data to be sent to the wrong endpoint. All events that do not belong to a specific
 * team will be sent to the endpoint which is set by the following function.
 */
const resetSegmentEndpoint = () => {
  const ANALYTICS_ENDPOINT = process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT
  const API_KEY = process.env.NEXT_PUBLIC_ANALYTICS_API_KEY
  AnalyticsUtils.overrideSegmentDestinationSettings(ANALYTICS_ENDPOINT, API_KEY)
}

/**
 * In order to send each team events to a different endpoint, we need to override the segment endpoint by the team
 * endpoint which is defined in the environment variables. This function overrides the Segment endpoint with the
 * team endpoint.
 * @param team - the team that the event is being sent by
 */
const setSegmentTeamEndpoint = (team: Team) => {
  const config = ANALYTICS_CONFIGS[team]

  if (!config || !config.endpoint || !config.key)
    throw new Error(
      `Missing analytics endpoint and API key environment variable for team: ${team}`
    )

  AnalyticsUtils.overrideSegmentDestinationSettings(config.endpoint, config.key)
}

const trackAnalyticsEvent = (
  eventName: string,
  eventProperties: (TrackEventProperties | ExperimentContext) & {team?: Team}
) => {
  try {
    const {team = Team.Default, ...propertiesWithoutTeam} = eventProperties
    setSegmentTeamEndpoint(team)
    AnalyticsUtils.trackEvent(eventName, propertiesWithoutTeam)
    resetSegmentEndpoint()
  } catch (error) {
    logger.error('Error tracking analytics event', error)
  }
}

const isTestEnvironment =
  process.env.STORYBOOK || process.env.NODE_ENV === 'test'

const middleware: MiddlewareType = store => next => async action => {
  const {dispatch, getState} = store
  const state = getState()
  const anonymousId = getAnonymousId(state)
  const timezone = getTimezone()
  const brand = getBrand(state)
  const cio = customerIo(brand)

  if (
    typeof window !== 'undefined' &&
    !isTestEnvironment &&
    !AnalyticsUtils.analyticsPlatformEnabled()
  ) {
    initializeAnalytics(brand)
  }

  if (!AnalyticsUtils.analyticsPlatformEnabled()) return next(action)

  switch (action.type) {
    case trackEvent.type: {
      const payload = action.payload as TrackEventPayload
      const analyticsEventName = createDataPipelineEventName(payload)
      const eventProperties = getEventProperties(state, payload)

      trackAnalyticsEvent(analyticsEventName, eventProperties)
      trackFullStoryEvent(analyticsEventName, eventProperties)

      logToDataDog({
        logName: eventProperties.name, // Use more descriptive full name for DD so that it's easier to navigate the logs
        metadata: eventProperties,
        level: 'info'
      })

      break
    }
    case TRACK_PAGE_VIEW: {
      AnalyticsUtils.trackPageView(action.pageName, action.meta)
      getFullStoryObject()?.identify?.(anonymousId || '', {
        pageName: action?.pageName
      })

      break
    }

    case TRACK_CIO_PAGE_VIEW: {
      const {id, ...params} = action.params

      cio.trackPageView({
        pageName: action.pageName,
        userId: id,
        anonymousId: anonymousId || '',
        ...params,
        timezone
      })

      break
    }

    case identifyAuthenticatedUser.type: {
      const user = getUser(state)
      const userId = getUserId(user)
      const userEmail = getUserEmail(user)
      const isPrivacyRestrictedUser = getIsPrivacyRestrictedUser(state)

      if (!userId) return

      // Push hashed email to GTM for Google Enhanced Conversions (only if not privacy-restricted)
      const shouldIdentifyUserInGTM =
        userEmail && typeof window !== 'undefined' && window.dataLayer

      if (shouldIdentifyUserInGTM && !isPrivacyRestrictedUser) {
        const sha256Email = await hashEmail(userEmail)
        window.dataLayer?.push({
          event: 'UserEmailHashed',
          sha256Email
        })
      }

      const anonymousId = getAnonymousId(state)
      const cognitoTraits = getUserIdentifiableTraits(state)
      const memberPlus = getCognitoIsMemberPlus(user)

      AnalyticsUtils.identify(userId, {
        memberPlus,
        user: cognitoTraits
      })

      try {
        await customIdentify({
          anonymousId: anonymousId || '',
          userId,
          customerDataEndpoint:
            process.env.NEXT_PUBLIC_ANALYTICS_CUSTOMER_DATA_ENDPOINT!,
          customerDataApiKey:
            process.env.NEXT_PUBLIC_ANALYTICS_CUSTOMER_DATA_API_KEY!,
          brandName: brand.name,
          cognitoTraits
        })
      } catch (error) {
        logToDataDog({
          logName: 'CustomIdentifyEventFailed',
          metadata: {
            errorType: (error as Error)?.message
          },
          level: 'info'
        })
      }

      break
    }

    case TRACK_EXPERIMENT_SERVED: {
      const {experimentId, variationId, isActive} = action

      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Experiment,
          team: Team.Shared,
          action: Action.Served,
          analyticsContext: {
            [AnalyticsContext.ExperimentContext]: {
              experimentId,
              variationId,
              isActive
            }
          }
        })
      )
      break
    }
    case trackRedirectOfferViewed.type: {
      const {offerId} = action.payload
      const analyticsContext = getRedirectOffersContexts(state, offerId)

      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.Offer,
          action: Action.Viewed,
          analyticsContext
        })
      )
      break
    }
    case trackRedirectOfferClicked.type: {
      const {offerId} = action.payload
      const analyticsContext = getRedirectOffersContexts(state, offerId)
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.Offer,
          action: Action.Clicked,
          analyticsContext
        })
      )
      break
    }
    case offersSearchSucceeded.type: {
      const analyticsContext = {
        [AnalyticsContext.SearchContext]: getSearchContext(state)
      }
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Succeeded,
          analyticsContext
        })
      )
      break
    }
    case offersSearchStarted.type: {
      const analyticsContext = {
        [AnalyticsContext.SearchContext]: getSearchContext(state)
      }
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Search,
          action: Action.Requested,
          analyticsContext
        })
      )
      break
    }
    default:
      break
  }

  return next(action)
}

export default middleware
